import { useAuth } from "@/context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  LogOut, Menu, X, LayoutGrid, Columns, LayoutDashboard, Package, FileText,
  ClipboardList, Target, Wrench, CalendarClock, Calendar, Users, CreditCard, UserCog,
  PanelLeftClose, PanelLeftOpen, Database, ShoppingCart, ListChecks, MapPin, BarChart3, Gauge, Building2,
} from "lucide-react";

export function PortalNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("pas_sb_collapsed") === "1");
  const [counts, setCounts] = useState({ overdue_followups: 0, open_work_orders: 0 });

  const isAdmin = user?.role === "admin";
  const isElevated = isAdmin || user?.role === "manager" || user?.role === "sales_manager";
  const isStaff = isElevated || user?.role === "sales";
  const path = location.pathname;
  const sp = new URLSearchParams(location.search);
  const salesTab = path === "/sales" ? (sp.get("tab") || "dashboard") : null;
  const serviceTab = path === "/service" ? (sp.get("tab") || "dispatch") : null;
  const instTab = path === "/instrumentation" ? (sp.get("tab") || "customers") : null;

  useEffect(() => {
    let active = true;
    api.get("/nav/counts").then(({ data }) => { if (active) setCounts(data); }).catch(() => {});
    return () => { active = false; };
  }, [path, location.search]);

  useEffect(() => {
    document.documentElement.style.setProperty("--sb-w", collapsed ? "4rem" : "16rem");
    localStorage.setItem("pas_sb_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => { setOpen(false); }, [location.pathname, location.search]);

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const go = (to) => { setOpen(false); navigate(to); };

  const sections = [
    {
      label: "OVERVIEW",
      items: [
        { label: "CRM Dashboard", to: "/crm", icon: LayoutGrid, testid: "nav-crm", active: path === "/crm" || path.startsWith("/crm/customers"), badge: counts.overdue_followups },
        { label: "Pipeline", to: "/crm/pipeline", icon: Columns, testid: "nav-pipeline", active: path === "/crm/pipeline" },
        { label: "My Day", to: "/crm/my-day", icon: MapPin, testid: "nav-my-day", active: path === "/crm/my-day" },
        { label: "Activity Report", to: "/crm/activity", icon: BarChart3, testid: "nav-activity-report", active: path === "/crm/activity" },
      ],
    },
    isElevated && {
      label: "SALES LOGIC",
      items: [
        { label: "Dashboard", to: "/sales?tab=dashboard", icon: LayoutDashboard, testid: "nav-sales-dashboard", active: salesTab === "dashboard" },
        { label: "Quotes", to: "/sales?tab=quotes", icon: FileText, testid: "nav-sales-quotes", active: salesTab === "quotes" },
        { label: "Sales Orders", to: "/sales?tab=orders", icon: ClipboardList, testid: "nav-sales-orders", active: salesTab === "orders" },
        { label: "Opportunities", to: "/sales?tab=opportunities", icon: Target, testid: "nav-sales-opps", active: salesTab === "opportunities" },
      ],
    },
    {
      label: "SERVICE LOGIC",
      items: [
        { label: "Dispatch Board", to: "/service?tab=dispatch", icon: CalendarClock, testid: "nav-service-dispatch", active: serviceTab === "dispatch" },
        { label: "Schedule", to: "/service?tab=schedule", icon: Calendar, testid: "nav-service-schedule", active: serviceTab === "schedule" },
        { label: "Work Orders", to: "/service?tab=orders", icon: Wrench, testid: "nav-service-orders", active: serviceTab === "orders", badge: counts.open_work_orders },
        { label: "Technicians", to: "/service?tab=techs", icon: Users, testid: "nav-service-techs", active: serviceTab === "techs" },
        ...(isElevated ? [
          { label: "PM Coverage", to: "/service?tab=coverage", icon: CalendarClock, testid: "nav-service-pm-coverage", active: serviceTab === "coverage" },
          { label: "Purchasing", to: "/purchasing", icon: ShoppingCart, testid: "nav-purchasing", active: path === "/purchasing" },
          { label: "Checklists", to: "/checklists", icon: ListChecks, testid: "nav-checklists", active: path === "/checklists" },
        ] : []),
      ],
    },
    isElevated && {
      label: "SHARED CORE",
      items: [
        { label: "Inventory Master", to: "/sales?tab=inventory", icon: Package, testid: "nav-inventory-master", active: salesTab === "inventory" },
      ],
    },
    isElevated && {
      label: "PEREGRINE INSTRUMENTATION",
      items: [
        { label: "Customers", to: "/instrumentation?tab=customers", icon: Building2, testid: "nav-inst-customers", active: instTab === "customers" },
        { label: "Modeling", to: "/instrumentation?tab=modeling", icon: Gauge, testid: "nav-inst-modeling", active: instTab === "modeling" },
      ],
    },
    {
      label: "CREDIT LOGIC",
      items: [
        { label: "Credit Applications", to: "/admin", icon: CreditCard, testid: "nav-credit", active: path === "/admin" },
      ],
    },
    isElevated && {
      label: "ADMIN",
      items: [
        { label: "Users", to: "/users", icon: UserCog, testid: "nav-users", active: path === "/users" },
        { label: "SQL Reports", to: "/reports", icon: Database, testid: "nav-reports", active: path === "/reports" },
      ],
    },
  ].filter(Boolean);

  if (!isStaff) return null;
  const hide = collapsed ? "md:hidden" : "";

  return (
    <>
      <div className="md:hidden sticky top-0 z-40 bg-[#0F172A] text-white flex items-center justify-between px-4 h-14">
        <button onClick={() => setOpen(true)} data-testid="sidebar-toggle" className="text-white"><Menu className="h-6 w-6" /></button>
        <span className="font-display font-bold text-sm tracking-tight">PEREGRINE COMPANIES</span>
        <span className="w-6" />
      </div>

      {open && <div className="md:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setOpen(false)} />}

      <aside data-testid="portal-sidebar" className={`fixed top-0 left-0 z-50 h-screen w-64 ${collapsed ? "md:w-16" : "md:w-64"} bg-[#0F172A] text-white flex flex-col transition-all duration-200 md:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="px-4 h-16 flex items-center gap-2.5 border-b border-white/10 shrink-0">
          <img src="/paslogo.png" alt="Peregrine Companies" className="h-9 w-auto shrink-0" />
          <div className={`leading-tight ${hide}`}>
            <div className="font-display font-extrabold text-sm tracking-tight">PEREGRINE COMPANIES</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-medium">Operations Hub</div>
          </div>
          <button onClick={() => setOpen(false)} className="md:hidden ml-auto text-zinc-400 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-5">
          {sections.map((sec) => (
            <div key={sec.label}>
              <div className={`px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 ${hide}`}>{sec.label}</div>
              <div className="space-y-0.5">
                {sec.items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <button key={it.to} onClick={() => go(it.to)} data-testid={it.testid} title={it.label}
                      className={`relative w-full flex items-center gap-3 py-2.5 px-3 rounded-md text-sm border-l-2 transition-colors ${collapsed ? "md:justify-center md:px-2" : ""} ${it.active ? "bg-white/10 text-white font-medium border-[#D4AF37]" : "text-zinc-400 hover:text-white hover:bg-white/5 border-transparent"}`}>
                      <Icon className={`h-4 w-4 shrink-0 ${it.active ? "text-[#D4AF37]" : ""}`} />
                      <span className={`flex-1 text-left truncate ${hide}`}>{it.label}</span>
                      {it.badge > 0 && (
                        <span data-testid={`${it.testid}-badge`} className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-[#EF4444] rounded-full ${collapsed ? "md:absolute md:top-1 md:right-1 md:min-w-[8px] md:h-2 md:w-2 md:p-0 md:text-transparent" : ""}`}>{it.badge}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="px-3 py-3 border-t border-white/10 flex items-center gap-2 shrink-0">
          <div className={`min-w-0 flex-1 ${hide}`}>
            <div className="text-sm font-medium truncate" data-testid="portal-nav-username">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-wide text-zinc-500">{(user?.role || "").replace("_", " ")}</div>
          </div>
          <button onClick={() => setCollapsed(!collapsed)} data-testid="sidebar-collapse" title={collapsed ? "Expand" : "Collapse"} className="hidden md:flex text-zinc-400 hover:text-white shrink-0">
            {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </button>
          <button onClick={handleLogout} data-testid="portal-logout-btn" title="Sign out" className="text-zinc-400 hover:text-[#EF4444] transition-colors shrink-0"><LogOut className="h-4 w-4" /></button>
        </div>
      </aside>
    </>
  );
}
